import { Vec2 } from "@base/math/Vec2";
import { Vec3 } from "@base/math/Vec3";
import { getKeys, mapCount, PartialRecord } from "@base/util/TypeUtil";
import { kederInfos, KederSize, kederSizes } from "@biz/definitions/KederDefinitions";
import { IMinMax } from "@biz/definitions/MinMax";
import { EdgeName } from "@biz/definitions/SunSailDefinitions";
import {
  getNetEdgeIndices, getNetEdgePointName, getNetPointIndex, INetData, INetEdgeData, NetEdgeColorType, netEdgeColorTypes, NetEyeletCornerOverride as NetCornerEyeletOverride, NetEyeletDistribution, NetEyeletPositionMode, NetEyeletType,
  netEyeletTypes, NetHemstitchSizeOption, netHemstitchSizeOptions, NetMultiConfigMode, netMultiConfigModes, NetPointName, NetSideFinishing, NetVelcroColor, netVelcroColors, NetVelcroSide, NetVelcroSideSelection, NetVelcroType
} from "@biz/items/net/NetDefinitions";
import { NetGeometryHelper } from "@biz/items/net/NetGeometryHelper";
import { NetShapeLogic } from "@biz/items/net/NetShapeLogic";
import { TriangleHelper } from "@biz/items/sunSail/logic/TriangleHelper";
import { IClothData } from "@biz/logic/ClothData";
import { ClothDataLogic } from "@biz/logic/ClothDataLogic";
import { getChecked } from "@biz/logic/getChecked";
import { ISCNetData } from "@biz/sc/net/ISCNetData";

const DOUBLE_CORNER_OFFSET = 0.05;

interface IEdgeEyeletInfoInternal {
  distances: number[];
  corners: ({ atSharedCorner?: boolean; } | undefined)[];
}

interface IEdgeEyeletInfo extends IEdgeEyeletInfoInternal {
  minOffset: number[]
}

export class NetLogic {
  static calcAngle(product: ISCNetData | INetData, p: NetPointName): number {
    const cornerPoints = NetGeometryHelper.calculateCornerPoints2d(product.dimensions, product.shapeType);
    if (cornerPoints.points) {
      const points = cornerPoints.points;
      const index = getNetPointIndex(p);
      const pre = points[(index - 1 + points.length) % points.length];
      const current = points[index];
      const next = points[(index + 1) % points.length];
      const angle = Vec2.calcAngle(pre, current, next);

      const preNext = Vec2.to(pre, next);
      const normal = Vec2.from(-preNext.y, preNext.x);
      const dot = Vec2.dot(normal, Vec2.to(pre, current));
      return dot < 0 ? Math.PI + angle : angle;
    }
    return 0;
  }

  static getMinMaxDimension(): IMinMax {
    return { min: 0.3, max: 50 } // https://plaspack.atlassian.net/browse/APP-1603
  }

  static getMaxArea(): number { // https://plaspack.atlassian.net/browse/APP-1603
    return 100;
  }

  static getMinMaxAngleDeg(): IMinMax {
    return { min: 15, max: 180 };
  }  

  static getCloth(product: ISCNetData | INetData): IClothData {
    return product.cloth || ClothDataLogic.getDefaultClothData(product.pd, product.featureLevel)!;
  }
  static getEdgeNames(netData: ISCNetData | INetData): EdgeName[] {
    return getKeys(netData.edges);
  }

  static getPointNames(netData: ISCNetData | INetData): NetPointName[] {
    return getKeys(netData.points);
  }

  static getEdgeConfigMode(net: INetData | ISCNetData): NetMultiConfigMode | undefined {
    return getChecked({
      selection: net.edgeConfigMode,
      validItems: NetLogic.getPossibleEdgeConfigModes(net),
      emptySelection: "individual"
    });
  }

  static getPossibleEdgeConfigModes(_net: INetData | ISCNetData): readonly NetMultiConfigMode[] {
    return netMultiConfigModes;
  }

  static getPointConfigMode(net: INetData | ISCNetData): NetMultiConfigMode | undefined {
    return getChecked({
      selection: net.pointConfigMode,
      validItems: NetLogic.getPossiblePointConfigModes(net),
      emptySelection: "individual"
    });
  }

  static getPossiblePointConfigModes(_net: INetData | ISCNetData): readonly NetMultiConfigMode[] {
    return netMultiConfigModes;
  }

  static getPossibleSideFinishing(_net: INetData | ISCNetData, _edgeName: EdgeName): NetSideFinishing[] {
    const items: NetSideFinishing[] = [];
    items.push("pe");
    items.push("peVelcro");
    items.push("profi");
    items.push("narrow");
    items.push("overlock");
    items.push("cut");
    items.push("keder");
    items.push("hemstitch");
    return items;
  }

  static getPossibleEdgeColor(net: INetData | ISCNetData, edgeName: EdgeName): NetEdgeColorType[] {
    const sideFinishingType = NetLogic.getSideFinishingType(net, edgeName);
    if (sideFinishingType === "profi") {
      return netEdgeColorTypes;
    }
    return [];
  }

  static getPossibleEyeletTypes(_net: INetData | ISCNetData): NetEyeletType[] {
    return netEyeletTypes;
  }

  static getSideFinishingEyeletSupport(sideFinishingType: NetSideFinishing): "side" | "corner" | false {
    switch (sideFinishingType) {
      case "cut": return false;
      case "overlock": return false;
      case "narrow": return false;
      case "keder": return "corner";
      case "hemstitch": return "corner";
      case "peVelcro": return "corner";
      case "pe": return "side";
      case "profi": return "side";
    }
    return false;
  }

  static getPossibleEyeletPositionModes(net: INetData | ISCNetData, edgeName: EdgeName): NetEyeletPositionMode[] {
    const sideFinishingType = NetLogic.getSideFinishingType(net, edgeName);
    const eyletSupport = NetLogic.getSideFinishingEyeletSupport(sideFinishingType)

    switch (eyletSupport) {
      case "side": return ["none", "standard", "corner", "distance", "countPerSide"];
      case "corner": return ["none", "corner"];
    }
    return [];
  }

  static #calcPossibleCornerEyletOverridesForPoint(net: INetData | ISCNetData, pointName: NetPointName) {
    const edgeNames = NetShapeLogic.getEdgeNamesForCornerIndex(net.shapeType, getNetPointIndex(pointName));

    const sideFinishing1 = NetLogic.getSideFinishingType(net, edgeNames.prev);
    const sideFinishing2 = NetLogic.getSideFinishingType(net, edgeNames.next);

    const mode1 = NetLogic.getSideFinishingEyeletSupport(sideFinishing1);
    const mode2 = NetLogic.getSideFinishingEyeletSupport(sideFinishing2);

    const cornerOverrides: NetCornerEyeletOverride[] = ["keep"];
    if (mode1 || mode2) {
      cornerOverrides.push("singleEyelet");
      cornerOverrides.push("dring");
    }
    if (mode1 === "side" && mode2 === "side") {
      cornerOverrides.push("doubleEyelet");
    }

    return cornerOverrides;
  }

  static getPossibleCornerEyeletOverrides(net: INetData | ISCNetData, pointName: NetPointName | "all"): { possible: NetCornerEyeletOverride[], displayed: NetCornerEyeletOverride[], disabled: NetCornerEyeletOverride[] } {
    if (pointName === "all") {
      const pointNames = NetShapeLogic.getPointNamesForShape(net.shapeType);
      const overrideMap: PartialRecord<NetCornerEyeletOverride, number> = {};
      for (const pn of pointNames) {
        const overrides = NetLogic.#calcPossibleCornerEyletOverridesForPoint(net, pn);
        for (const o of overrides) {
          overrideMap[o] = (overrideMap[o] || 0) + 1;
        }
      }
      const possible: NetCornerEyeletOverride[] = [];
      const disabled: NetCornerEyeletOverride[] = [];
      const displayed: NetCornerEyeletOverride[] = [];

      for (const key of getKeys(overrideMap)) {
        const item = overrideMap[key];
        displayed.push(key);
        if (item === pointNames.length)
          possible.push(key);
        else if ((item || 0) > 0)
          disabled.push(key)
      }
      return {
        possible,
        disabled,
        displayed
      }
    } else {
      const possible: NetCornerEyeletOverride[] = [...NetLogic.#calcPossibleCornerEyletOverridesForPoint(net, pointName), "none"];
      return {
        possible,
        displayed: possible,
        disabled: [],
      }
    }
  }

  static getPossibleVelcroColor(net: INetData | ISCNetData, edgeName: EdgeName): NetVelcroColor[] {
    const sideFinishingType = NetLogic.getSideFinishingType(net, edgeName);
    if (sideFinishingType === "peVelcro") {
      return netVelcroColors;
    }
    return [];
  }

  static getPossibleVelcroSide(net: INetData | ISCNetData, edgeName: EdgeName): NetVelcroSideSelection[] {
    const sideFinishingType = NetLogic.getSideFinishingType(net, edgeName);
    if (sideFinishingType === "peVelcro") {
      return ["back", "front", "both"];
    }
    return []
  }


  static getVelcroSide(net: INetData | ISCNetData, edgeName: EdgeName): NetVelcroSideSelection | undefined {
    const velcroSide = NetLogic.getEdge(net, edgeName)?.velcroSide;
    return getChecked({
      selection: velcroSide,
      validItems: NetLogic.getPossibleVelcroSide(net, edgeName),
      emptySelection: "__selectionRequired__"
    })
  }

  static getPossibleVelcroTypes(net: INetData | ISCNetData, edgeName: EdgeName, side: NetVelcroSide): NetVelcroType[] {
    const sideFinishingType = NetLogic.getSideFinishingType(net, edgeName);
    if (sideFinishingType === "peVelcro") {
      const sideSelection = NetLogic.getVelcroSide(net, edgeName);
      if (sideSelection === side || sideSelection === "both") {
        return ["loop", "hook"];
      }
    }
    return []
  }

  static getPossibleHemstitchSizeOptions(net: INetData | ISCNetData, edgeName: EdgeName): NetHemstitchSizeOption[] {
    const sideFinishingType = NetLogic.getSideFinishingType(net, edgeName);
    if (sideFinishingType === "hemstitch") {
      return netHemstitchSizeOptions;
    }
    return []
  }

  static getPossibleKederSizes(net: INetData | ISCNetData, edgeName: EdgeName): KederSize[] {
    const sideFinishingType = NetLogic.getSideFinishingType(net, edgeName);

    if (sideFinishingType !== "keder") {
      return [];
    }
    return kederSizes;
  }

  static getPossibleKederRails(net: INetData | ISCNetData, edgeName: EdgeName): string[] {
    const kederSize = NetLogic.getKederSize(net, edgeName);
    if (!kederSize)
      return [];
    return kederInfos[kederSize]?.railArticles || [];
  }

  static getSideFinishingType(net: INetData | ISCNetData, edgeName: EdgeName): NetSideFinishing {
    const sideFinishingType = NetLogic.getEdge(net, edgeName)?.sideFinishingType;

    return getChecked({
      selection: sideFinishingType,
      validItems: NetLogic.getPossibleSideFinishing(net, edgeName),
      emptySelection: "__selectionRequired__"
    })!
  }

  static getEdgeColor(net: INetData | ISCNetData, edgeName: EdgeName): NetEdgeColorType | undefined {
    const edgeColorType = NetLogic.getEdge(net, edgeName)?.edgeColor;
    return getChecked({
      selection: edgeColorType,
      validItems: NetLogic.getPossibleEdgeColor(net, edgeName),
      emptySelection: "__selectionRequired__"
    })
  }

  static getEyeletType(net: INetData | ISCNetData): NetEyeletType | undefined {
    return getChecked({
      selection: net.eyeletType,
      validItems: NetLogic.getPossibleEyeletTypes(net),
      emptySelection: "__selectionRequired__"
    })
  }

  static getEyeletPositionMode(net: INetData | ISCNetData, edgeName: EdgeName): NetEyeletPositionMode | undefined {
    const eyeletPosition = NetLogic.getEdge(net, edgeName)?.eyeletPositionMode;
    return getChecked({
      selection: eyeletPosition,
      validItems: NetLogic.getPossibleEyeletPositionModes(net, edgeName),
      emptySelection: "__selectionRequired__"
    })
  }

  static getCornerEyeletOverride(net: INetData | ISCNetData, point: NetPointName): NetCornerEyeletOverride {
    const pointEdgeType = net.points?.[point]?.eyeletOverride;

    return getChecked({
      selection: pointEdgeType,
      validItems: NetLogic.getPossibleCornerEyeletOverrides(net, point).possible,
      emptySelection: "__selectionRequired__"
    })!
  }
  static getVelcroColor(net: INetData | ISCNetData, edgeName: EdgeName): NetVelcroColor | undefined {
    const velcroColor = NetLogic.getEdge(net, edgeName)?.velcroColor;
    return getChecked({
      selection: velcroColor,
      validItems: NetLogic.getPossibleVelcroColor(net, edgeName),
      emptySelection: "__selectionRequired__"
    })
  }

  static getVelcroType(net: INetData | ISCNetData, edgeName: EdgeName, side: NetVelcroSide): NetVelcroType {
    const edge = NetLogic.getEdge(net, edgeName);
    const velcroType = side === "front" ? edge?.velcroTypeFront : edge?.velcroTypeBack;
    return getChecked({
      selection: velcroType,
      validItems: NetLogic.getPossibleVelcroTypes(net, edgeName, side),
      emptySelection: "__selectionRequired__"
    })!
  }

  static getHemstitchSizeOption(net: INetData | ISCNetData, edgeName: EdgeName): NetHemstitchSizeOption | undefined {
    const hemstitchSizeOption = NetLogic.getEdge(net, edgeName)?.hemstitchSizeOption;
    return getChecked({
      selection: hemstitchSizeOption,
      validItems: NetLogic.getPossibleHemstitchSizeOptions(net, edgeName),
      emptySelection: "__selectionRequired__"
    })
  }

  static getKederSize(net: INetData | ISCNetData, edgeName: EdgeName): KederSize | undefined {
    const validItems = NetLogic.getPossibleKederSizes(net, edgeName);

    return getChecked({
      selection: NetLogic.getEdge(net, edgeName)?.kederSize ?? "7.5",
      validItems,
      emptySelection: "__selectionRequired__"
    })
  }
  static getKederRail(net: INetData | ISCNetData, edgeName: EdgeName): string | undefined {
    return getChecked({
      selection: NetLogic.getEdge(net, edgeName)?.kederRail,
      validItems: NetLogic.getPossibleKederRails(net, edgeName),
      emptySelection: undefined
    })
  }

  static getEdge(net: INetData | ISCNetData, edgeName: EdgeName): INetEdgeData | undefined {
    if (edgeName[0] < edgeName[1])
      return net.edges[edgeName];
    return net.edges[(edgeName[1] + edgeName[0]) as EdgeName];
  }

  static getEyeletUserCount(net: INetData | ISCNetData, edgeName: EdgeName): number {
    return NetLogic.getEdge(net, edgeName)?.eyeletUserCount ?? 3;
  }

  static getEyeletUserDistance(net: INetData | ISCNetData, edgeName: EdgeName): number {
    return NetLogic.getEdge(net, edgeName)?.eyeletUserDistance ?? 0.3;
  }


  static getHemstitchSize(net: INetData | ISCNetData, edgeName: EdgeName): number {
    if (NetLogic.getHemstitchSizeOption(net, edgeName) === "standard")
      return 0.04;
    return NetLogic.getEdge(net, edgeName)?.hemstitchUserSize || 0.05;
  }

  static getHemstitchSizeValue(net: INetData | ISCNetData, edgeName: EdgeName): number {
    if (NetLogic.getSideFinishingType(net, edgeName) !== "hemstitch")
      return 0;

    return NetLogic.getHemstitchSize(net, edgeName);
  }

  static calculateEyeletCountForRule(length: number, eyeletDistance: number, distributionRule: NetEyeletDistribution): number {
    const countWithComma = (length / eyeletDistance) + 1;
    switch (distributionRule) {
      case "standard": {
        if (countWithComma % 1 >= 0.4)
          return Math.ceil(countWithComma);
        return Math.floor(countWithComma);
      }
      case "evenly": {
        return Math.ceil(countWithComma);
      }
    }
    throw new Error("calculateEyeletCount: Not implemented for positionRule " + distributionRule);
  }

  static #calcEyeletStandardDistances(dist: number): IEdgeEyeletInfoInternal {
    const count = NetLogic.calculateEyeletCountForRule(dist, 0.5, "standard")
    if (count === 1) return { distances: [dist * 0.5], corners: [undefined, undefined] };
    return {
      distances: mapCount(count, (i) => {
        if (i === count - 1) return dist;
        if (i === count - 2) return (dist + ((i - 1) * 0.5)) * 0.5;
        return i * 0.5
      }),
      corners: [{ atSharedCorner: true }, { atSharedCorner: true }]
    }
  }

  static #calcEyeletCountPerSideDistances(dist: number, count: number): IEdgeEyeletInfoInternal {
    if (count === 1) return { distances: [dist * 0.5], corners: [undefined, undefined] };
    return {
      distances: mapCount(count, (i) => (i * dist) / (count - 1)),
      corners: [{ atSharedCorner: true }, { atSharedCorner: true }]
    }
  }

  static #calcEyeletDistanceDistances(dist: number, count: number): IEdgeEyeletInfoInternal {
    if (count === 1) return { distances: [dist * 0.5], corners: [undefined, undefined] };
    return {
      distances: mapCount(count, (i) => (i * dist) / (count - 1)),
      corners: [{ atSharedCorner: true }, { atSharedCorner: true }]
    }
  }

  static #calcEyeletCornerDistances(dist: number): IEdgeEyeletInfoInternal {
    return {
      distances: [0, dist],
      corners: [{ atSharedCorner: true }, { atSharedCorner: true }]
    }
  }

  // Eyelets can be added via edge settings and corner settings.
  // * Edge eyelets can only be added via edge settings
  // * Corner eyelets can be added via edge and/or corner settings
  // * ! Corner eyelets can be added via edge settings and removed via corner settings !
  // -----
  // * Calculations of eyelets positions is a little bit complicated
  // * first there ist this minOffset that defines where the first eyelet can be positioned (usually the edge eyelet)
  // * starting from the edge eyelets the other eyelets are positioned in a certain distance
  // * e.g. first 3.5cm, second 53.5cm
  // * if there is no edge eyelets, the position should be measured from the outer corner
  // * (eg. first 50 cm, (not 53.5 cm)
  // * if the corner eyelet is a double eyelt, the first eyelet - DOUBLE_CORNER_OFFSET is added to the minOffset
  // *(e.g. first: 8.5cm, second 58.5cm)
  // * availaableSpace for calculating the number of eyelets and evenly distances, depends on the corner eyelet (no, yes, double)
  // * since corners and edges can be configurated individually for edges and corners - there can be all combinations of edge/corner eyelet positions
  static calcEdgeEyeletInfos(net: INetData | ISCNetData, edgeName: EdgeName): IEdgeEyeletInfo | undefined {
    if (!net.dimensions)
      return undefined;

    const cornerPoints = NetGeometryHelper.calculateCornerPoints3d(net.dimensions, net.shapeType);
    if (cornerPoints.error)
      throw new Error("calcEyeletCountForEdge: No corner points: " + cornerPoints.error);

    const borderPoints = NetGeometryHelper.calcEyeletEdgePoints(cornerPoints.points, edgeName, 0.02);
    const outAB = Vec3.to(borderPoints[0].out, borderPoints[1].out);
    const minOffset: number[] = [
      Vec3.len(Vec3.project(Vec3.to(borderPoints[0].out, borderPoints[0].in), outAB)),
      Vec3.len(Vec3.project(Vec3.to(borderPoints[1].out, borderPoints[1].in), outAB))
    ]

    const startOffset: number[] = [];
    const cornerEyletTypes = [NetLogic.getCornerEyeletOverride(net, getNetEdgePointName(edgeName, 0)), NetLogic.getCornerEyeletOverride(net, getNetEdgePointName(edgeName, 1))];
    for (let i = 0; i < 2; i++) {
      switch (cornerEyletTypes[i]) {
        case "none": startOffset[i] = 0; break;
        case "doubleEyelet": startOffset[i] = minOffset[i] + DOUBLE_CORNER_OFFSET; break;
        default: startOffset[i] = minOffset[i];
      }
    }

    const fullSideWidth = Vec3.len(borderPoints[0].out, borderPoints[1].out);
    const availableSideDist = fullSideWidth - startOffset[0] - startOffset[1];

    const sideInfo = NetLogic.#calcEyeletEdgeInfoInternal(net, edgeName, availableSideDist)
    for (let i = 0; i < 2; i++) {
      switch (cornerEyletTypes[i]) {
        case "none": {
          if (sideInfo.corners?.[i]) {
            sideInfo.distances.splice(i === 0 ? 0 : sideInfo.distances.length - 1, 1);
            sideInfo.corners[i] = undefined;
          }
          break;
        }
        case "singleEyelet":
        case "doubleEyelet":
        case "dring": {
          if (!sideInfo.corners?.[i]) {
            sideInfo.distances.splice(i === 0 ? 0 : sideInfo.distances.length, 0, i === 0 ? 0 : availableSideDist);
          }
          sideInfo.corners[i] = { atSharedCorner: cornerEyletTypes[i] !== "doubleEyelet" }
          break;
        }
      }
    }

    if (!sideInfo.distances.length)
      return undefined;

    if (startOffset[0]) {
      for (let i = 0; i < sideInfo.distances.length; i++)
        sideInfo.distances[i] += startOffset[0];
    }

    return {
      ...sideInfo,
      minOffset
    }
  }

  static #calcEyeletEdgeInfoInternal(net: INetData | ISCNetData, edgeName: EdgeName, dist: number): IEdgeEyeletInfoInternal {
    const eyeletPositionMode = NetLogic.getEyeletPositionMode(net, edgeName);
    switch (eyeletPositionMode) {
      case "standard": return NetLogic.#calcEyeletStandardDistances(dist);
      case "countPerSide": return NetLogic.#calcEyeletCountPerSideDistances(dist, NetLogic.getEyeletUserCount(net, edgeName));
      case "distance": return NetLogic.#calcEyeletDistanceDistances(dist, NetLogic.calculateEyeletCountForRule(dist, NetLogic.getEyeletUserDistance(net, edgeName), "evenly"));
      case "corner": return NetLogic.#calcEyeletCornerDistances(dist);
    }
    return { distances: [], corners: [] };
  }

  static calcEyelets(net: INetData | ISCNetData): {
    total: number;
    totalCustom: number;
    shared: {
      count: number;
      customCount: number;
    }[]
  } {

    const pointCount = NetShapeLogic.getPointCount(net.shapeType);
    const shared: { count: number; customCount: number }[] = mapCount(pointCount, (_index) => ({ count: 0, customCount: 0 }))

    let total = 0;
    let totalCustom = 0;

    for (let i = 0; i < pointCount; i++) {
      const edgeName = NetShapeLogic.getEdgeNameForCornerIndex(net.shapeType, i);
      const isCustom = ["countPerSide", "distance"].includes(NetLogic.getEyeletPositionMode(net, edgeName) || "");

      const edgeIndices = getNetEdgeIndices(edgeName);
      const eyeletDistances = NetLogic.calcEdgeEyeletInfos(net, edgeName);
      if (eyeletDistances) {
        if (eyeletDistances.distances.length >= 2) {
          for (let c = 0; c < 2; c++) {
            if (eyeletDistances?.corners[c]?.atSharedCorner) {
              const item = shared[edgeIndices[c]];
              item.count++;
              if (isCustom)
                item.customCount++;
            }
          }
        }
        total += eyeletDistances.distances.length;
        if (isCustom)
          totalCustom += eyeletDistances.distances.length;
      }
    }

    for (let i = 0; i < shared.length; i++) {
      if (shared[i].count > 1) {
        total--;
        if (shared[i].customCount > 0)
          totalCustom--;
      }
    }
    return { total, totalCustom, shared };
  }

  static calcArea(net: INetData | ISCNetData): number {
    const shape = net.shapeType;
    const ab = net.dimensions?.ab || 0;
    const bc = net.dimensions?.bc || 0;
    const ac = net.dimensions?.ac || 0;
    switch (shape) {
      case "triangle": return TriangleHelper.calcArea(ab, bc, ac);
      case "rectangle": return ab * bc;
      case "quad": return TriangleHelper.calcArea(ab, bc, ac) + TriangleHelper.calcArea(net.dimensions?.cd || 0, net.dimensions?.ad || 0, ac);
    }
  }
}